import React from 'react'
import type { LayoutState } from './types'
import { CompactLayoutContext } from './CompactLayoutContext'

/**
 * A component that can be used to create a state of responsive behavior.
 *
 * It listens to all instances of useCompactOnOverflow() in its sub-tree.
 * Once at least one of them "requests" compact state,
 * it signals it to the responsive components via useCompactable() state
 *
 */
export const CompactLayoutProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [layoutStateRequests, setLayoutStateRequests] = React.useState<Record<string, LayoutState>>(
    {},
  )

  const layoutState = React.useMemo(() => {
    const condensed = Object.values(layoutStateRequests).some(state => state === 'compact')
    return condensed ? 'compact' : 'spacious'
  }, [layoutStateRequests])

  const requestLayoutState = React.useCallback((id: string, state: LayoutState) => {
    setLayoutStateRequests(currentState => ({ ...currentState, [id]: state }))
  }, [])

  const unregisterLayoutState = React.useCallback((id: string) => {
    setLayoutStateRequests(currentState => {
      const newState = { ...currentState }
      delete newState[id]
      return newState
    })
  }, [])

  return (
    <CompactLayoutContext.Provider
      value={{ layoutState, requestLayoutState, unregisterLayoutState }}
    >
      {children}
    </CompactLayoutContext.Provider>
  )
}
